import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { CultivarAreaCard, OrchardMap, SatellitePanel, SoilSamplesPanel } from '../../components/orchard';
import { Layers, Plus, Edit, Trash2, Loader2, Sprout, Info } from 'lucide-react';
import orchardService from '../../services/orchard';
import type { Block, Farm, CultivarArea, GeoJSONPolygon } from '../../types/orchard';

function BlockDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orgIdParam = searchParams.get('org_id');
  const targetOrgId = orgIdParam ? parseInt(orgIdParam, 10) : undefined;
  const orgQs = targetOrgId ? `&org_id=${targetOrgId}` : '';
  const [block, setBlock] = useState<Block | null>(null);
  const [farm, setFarm] = useState<Farm | null>(null);
  const [areas, setAreas] = useState<CultivarArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const blockId = Number(id);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const blockResponse = await orchardService.getBlock(blockId, targetOrgId);
      if (!blockResponse.success || !blockResponse.data) {
        setError('Failed to load block');
        return;
      }
      const blockData = blockResponse.data;
      setBlock(blockData);

      // Load farm and cultivar areas in parallel
      const [farmResponse, areasResponse] = await Promise.all([
        orchardService.getFarm(blockData.farm_id, targetOrgId),
        orchardService.getCultivarAreas({ block_id: blockId, org_id: targetOrgId }),
      ]);

      if (farmResponse.success && farmResponse.data) {
        setFarm(farmResponse.data);
      }
      if (areasResponse.success && areasResponse.data) {
        setAreas(areasResponse.data as CultivarArea[]);
      }
    } catch {
      setError('Failed to load block data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [blockId, targetOrgId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await orchardService.deleteBlock(blockId);
      if (response.success) {
        navigate(block?.farm_id ? `/orchard/farms/${block.farm_id}` : '/orchard');
      } else {
        setError('Failed to delete block');
        setShowDeleteConfirm(false);
      }
    } catch {
      setError('Failed to delete block. Please try again.');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Block"
          subtitle="Loading..."
          icon={<Layers className="h-6 w-6 text-white" />}
          showBackButton
        />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !block) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Block"
          subtitle="Error"
          icon={<Layers className="h-6 w-6 text-white" />}
          showBackButton
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-error-50 border border-error-200 rounded-xl p-6 text-center">
            <p className="text-error-700 mb-4">{error || 'Block not found'}</p>
            <Button variant="outline" onClick={loadData}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  const totalTrees = areas.reduce((sum, a) => sum + (a.tree_count ?? 0), 0);

  // Build map data from block + farm + areas
  const farmBoundary = farm?.boundary_geometry;
  const blockAsMapItem: Block[] = [block];
  const farmBoundaryForMap: GeoJSONPolygon | undefined = farmBoundary;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={block.name}
        subtitle={block.farm_name ? `Block in ${block.farm_name}` : 'Block Details'}
        icon={<Layers className="h-6 w-6 text-white" />}
        showBackButton
        actionButtons={
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(`/orchard/cultivar-areas/new?block_id=${block.id}&farm_id=${block.farm_id}${orgQs}`)}
            >
              <Plus className="h-4 w-4" />
              New Area
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/orchard/blocks/${block.id}/edit`)}
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {error && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <p className="text-error-700">{error}</p>
          </div>
        )}

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="bg-error-50 border border-error-300 rounded-xl p-6">
            <h3 className="text-base font-semibold text-error-900 mb-2">Delete Block?</h3>
            <p className="text-sm text-error-700 mb-4">
              This will permanently delete <strong>{block.name}</strong> and all its cultivar areas.
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button variant="danger" isLoading={deleting} onClick={handleDelete}>
                Yes, Delete Block
              </Button>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Breadcrumb */}
        {farm && (
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <button
              className="text-primary-600 hover:underline"
              onClick={() => navigate(`/orchard/farms/${farm.id}`)}
            >
              {farm.name}
            </button>
            <span>›</span>
            <span className="text-neutral-700 font-medium">{block.name}</span>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-neutral-900">
              {block.area_hectares != null ? Number(block.area_hectares).toFixed(2) : '—'}
            </p>
            <p className="text-xs text-neutral-500 mt-1">Hectares</p>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-success-600">{areas.length}</p>
            <p className="text-xs text-neutral-500 mt-1">Cultivar Areas</p>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-purple-600">{totalTrees.toLocaleString()}</p>
            <p className="text-xs text-neutral-500 mt-1">Total Trees</p>
          </div>
        </div>

        {/* Map */}
        <div className="rounded-xl overflow-hidden border border-neutral-200 shadow-sm" style={{ height: '420px' }}>
          <OrchardMap
            farmBoundary={farmBoundaryForMap}
            blocks={blockAsMapItem}
            cultivarAreas={areas}
            selectedEntityId={block.id}
            selectedEntityType="block"
            center={
              block.center_point
                ? [block.center_point.lat, block.center_point.lng]
                : farm?.center_point
                ? [farm.center_point.lat, farm.center_point.lng]
                : undefined
            }
            zoom={16}
          />
        </div>

        {/* Description */}
        {block.description && (
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Info className="h-4 w-4 text-neutral-500" />
                <h3 className="text-sm font-semibold text-neutral-700">Notes</h3>
              </div>
              <p className="text-sm text-neutral-600">{block.description}</p>
            </CardContent>
          </Card>
        )}

        {/* Sentinel-2 over the block, from the farm's images */}
        {targetOrgId == null && block.boundary_geometry && (
          <SatellitePanel
            farmId={block.farm_id}
            farmName={farm?.name ?? block.farm_name}
            target={{ type: 'block', id: block.id, name: block.name, boundary: block.boundary_geometry }}
            outlines={areas.map((a) => a.boundary_geometry)}
          />
        )}

        {/* Soil analysis — lab results for the block and its plantings */}
        <SoilSamplesPanel blockId={block.id} />

        {/* Cultivar Areas */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sprout className="h-5 w-5 text-success-600" />
              <h2 className="text-lg font-semibold text-neutral-900">Cultivar Areas</h2>
              <span className="text-sm text-neutral-500">({areas.length})</span>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(`/orchard/cultivar-areas/new?block_id=${block.id}&farm_id=${block.farm_id}${orgQs}`)}
            >
              <Plus className="h-4 w-4" />
              Add Area
            </Button>
          </div>

          {areas.length === 0 ? (
            <div className="bg-white rounded-xl border border-neutral-200 p-10 text-center">
              <div className="h-12 w-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
                <Sprout className="h-6 w-6 text-neutral-400" />
              </div>
              <h3 className="text-base font-semibold text-neutral-900 mb-1">No Cultivar Areas Yet</h3>
              <p className="text-sm text-neutral-600 mb-4">
                Add cultivar areas to track varieties, tree counts and spacing.
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate(`/orchard/cultivar-areas/new?block_id=${block.id}&farm_id=${block.farm_id}${orgQs}`)}
              >
                <Plus className="h-4 w-4" />
                Create First Area
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {areas.map((area) => (
                <CultivarAreaCard key={area.id} area={area} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BlockDetail;
